/* eslint-disable import/no-extraneous-dependencies */
import { isPlainObject, castArray, isNil } from 'lodash';
import { isMongoId, isEmail, isAlphanumeric, isMobilePhone, isJSON, isURL } from 'validator';
import dayjs from 'dayjs';
import type {
  RuleObject,
  Rule,
  BaseRule,
  RuleRender,
  ValidatorRule,
  ArrayRule,
  RuleType as _RuleType,
} from 'rc-field-form/lib/interface';

type RuleType = _RuleType | 'array';
export type { Rule, RuleType };

// NOTE: `validator` functions are async because it expects a Promise return value

function transformRules(
  extra: PlainObject<string> & { type?: RuleType | 'array' },
  rules: Rule[] = [],
) {
  const { type, ...rest } = extra;
  return rules.map((rule) => {
    if (!isPlainObject(rule)) return rule;
    rule = { ...rest, ...rule };
    if (type && (!isNil(rule.min) || !isNil(rule.max) || !isNil(rule.len))) {
      rule = { ...rule, type };
    }
    return rule;
  });
}

/**
 * NOTE: It is important to have a default value of empty array if using an array rule
 * @see https://www.npmjs.com/package/async-validator#defaultfield
 */
function arrayOfRule(rule: RuleObject) {
  return {
    type: 'array' as const,
    options: { first: true },
    defaultField: rule,
  };
}

const alphaNumeric: ValidatorRule = {
  validator: async (rule, val) => {
    if (isAlphanumeric(val, 'en-US')) return true;
    throw new Error('Special characters and Spaces are not allowed');
  },
};

const email: BaseRule = { type: 'email' };

const mobile: ValidatorRule = {
  validator: async (rule, val) => {
    if (!val || isMobilePhone(val, 'any', { strictMode: true })) {
      return true;
    }
    throw new Error('Invalid mobile number. Use format +91XXXXXXXXXX');
  },
};

const equal =
  (field: string): RuleRender =>
    ({ getFieldValue }) => ({
      validator: async (rule, val) => {
        if (getFieldValue(field) === val) {
          return true;
        }
        // @ts-ignore
        throw new Error(`${rule.field} must be equal to ${field}`);
      },
    });

const gteField =
  (field: string | (string | number)[], fieldLabel: string, compFieldLabel: string): RuleRender =>
    ({ getFieldValue }) => ({
      validator: async (rule, val) => {
        if (isNil(val) || getFieldValue(field) <= val) {
          return true;
        }
        throw new Error(`${fieldLabel} must be greater than equal to ${compFieldLabel}`);
      },
    });

const lteField =
  (field: string | (string | number)[], fieldLabel: string, compFieldLabel: string): RuleRender =>
    ({ getFieldValue }) => ({
      validator: async (rule, val) => {
        if (isNil(val) || getFieldValue(field) >= val) {
          return true;
        }
        throw new Error(`${fieldLabel} must be lesser than equal to ${compFieldLabel}`);
      },
    });

const dateRange = (allowedRange: number): ValidatorRule => ({
  validator: async (rule, val) => {
    if (val && val.length === 2) {
      if (val[0] && val[1]) {
        if (dayjs(val[1]).diff(dayjs(val[0]), 'day') > allowedRange)
          throw new Error(`Allowed date range is ${allowedRange} days`);
      }
    }
    return true;
  },
});

const max = (val: number): BaseRule => ({ max: val });

const min = (val: number): BaseRule => ({ min: val });

const decimalLength = (allowedDecimalLength = 5): ValidatorRule => ({
  validator: async (rule, val) => {
    if (!val) return;
    const valString = val.toString();
    const [, decimalPart] = valString.split('.');
    if (decimalPart && decimalPart.length > allowedDecimalLength) {
      throw new Error(`${allowedDecimalLength} decimal places are allowed at most.`);
    }
  },
});

/** Checks for only whitespace as value */
const noWhitespace: BaseRule = { whitespace: true };

const noWhitespaces = arrayOfRule(noWhitespace);

const password: BaseRule = {
  pattern: /(?=.*\d)(?=.*[a-z])(?=.*[A-Z]).*/,
  message: 'Password must contain: numbers, uppercase and lowercase letters',
};

const range = (minimum: number, maximum: number): BaseRule => ({ min: minimum, max: maximum });

const required: BaseRule = { required: true };

const requiredArray: ArrayRule = { required: true, type: 'array', min: 1 };

const requiredArrayValidator = (label: string): ValidatorRule => ({
  validator: async (rule, value) => {
    if (!value?.length) throw new Error(`${label} is required`);
    return true;
  },
});

type GetOptionsFn = () => Promise<PropPresets.OptionObject[]>;
const getSearchRule = (getOptions: GetOptionsFn): ValidatorRule => ({
  validator: async (rule, value) => {
    if (value === undefined || (Array.isArray(value) && !value.length)) return true;

    const validOptions = (await getOptions()).map((o) => o.value);
    const currValues = castArray(value);

    const errors = currValues
      .map((val) => {
        if (validOptions.includes(val)) return false;
        return true;
      })
      .filter(Boolean);

    if (errors.length) throw new Error('Invalid ID or No items found for given filters');
    return true;
  },
});

const url: BaseRule = { type: 'url' };

const mongoId: ValidatorRule = {
  validator: async (rule, value) => {
    if (!value || isMongoId(value)) return true;
    throw new Error('Invalid ID');
  },
};

const mongoIds = arrayOfRule(mongoId);

const mongoIdOrEmail: ValidatorRule = {
  validator: async (rule, value) => {
    if (isMongoId(value)) return true;
    if (isEmail(value)) return true;
    throw new Error('Invalid ID or Email');
  },
};

const validHtml: ValidatorRule = {
  validator: async (rule, val: string) => {
    if (!val) return Promise.resolve(true);
    if (val.match(/<img/g) && !val.match(/<img[^<>]+data-src-id=".+?".*?>/g)) {
      throw new Error('Add images using editor');
    }
    if (
      val.match(/<table/g) &&
      !val.match(/<figure class="table.*?"[^<>]*><table[^<>]*>.*?<\/table><\/figure>/g)
    ) {
      throw new Error('Add tables using editor');
    }
    if (!val.match(/^<[^<>]+>(?:.|\n)*<\/[^<>]+>$/)) {
      throw new Error('Wrap content with <p></p>');
    }

    return Promise.resolve(true);
  },
};

const jsonOrUrl: ValidatorRule = {
  validator: async (rule, val) => {
    if (!val || isJSON(val) || isURL(val)) return true;
    throw new Error('Invalid JSON or URL');
  },
};

const json: ValidatorRule = {
  validator: async (rule, value) => {
    if (!value || isJSON(value)) return true;
    throw new Error('Invalid JSON');
  },
};

export {
  alphaNumeric,
  dateRange,
  email,
  mobile,
  equal,
  max,
  min,
  noWhitespace,
  noWhitespaces,
  password,
  range,
  required,
  requiredArray,
  requiredArrayValidator,
  getSearchRule,
  url,
  gteField,
  lteField,
  mongoId,
  mongoIds,
  mongoIdOrEmail,
  arrayOfRule,
  transformRules,
  decimalLength,
  isMongoId,
  validHtml,
  jsonOrUrl,
  json,
};
